import * as React from 'react';
import { PaginatedUserContext } from '../shared/paginated-user-context';
import useRestService from '../shared/rest-service';
import User from '../models/user';
import FetchOptions from '../components/management/pagination/fetch-options';
import { UserSortingOptions } from '../models/enums/adminSortingOptions';

/**
 * usePaginatedUsers
 * @function usePaginatedUsers - Custom hook for the user management page, with pagination context
 */
const usePaginatedUsers = () => {
  const { loading, dispatch, results, totalResults } = React.useContext(PaginatedUserContext);
  const { getAuth } = useRestService();

  /**
   * fetchUsersPage
   * @function fetchUsers - Fetches the users from the backend and sets them to the state
   * @param options - FetchOptions
   */
  const fetchUsers = ({ currentPage, pageSize, searchTerm, orderColumn, orderDirection }: FetchOptions) => {
    dispatch({ loading: true });
    let queryParams = `?page=${currentPage}&pageSize=${pageSize}`;
    if (!(searchTerm === null || searchTerm === undefined) && searchTerm.trim() !== '') {
      queryParams += `&searchTerm=${searchTerm}`;
    }
    if (orderColumn != null) {
      queryParams += `&sort=${orderColumn}`;
    }
    if (orderDirection != null) {
      queryParams += `&sortDirection=${orderDirection}`;
    }
    return getAuth(`api/users${queryParams}`)
      .then((response: { results: User[]; totalItems: number }) => {
        dispatch({
          loading: false,
          currentPage: currentPage,
          pageSize: pageSize,
          results: response.results,
          totalResults: response.totalItems,
          orderColumn,
          orderDirection,
          searchTerm
        });
      })
      .catch(() => console.log('Failed to get users.'));
  };

  /**
   * fetchAllUsers
   * @function fetchAllUsers - Fetches all users without pagination
   * @param options fetch options object: searchTerm, orderColumn, orderDirection
   */
  const fetchAllUsers = ({
    searchTerm,
    orderColumn,
    orderDirection
  }: {
    searchTerm: string;
    orderColumn: UserSortingOptions;
    orderDirection: UserSortingOptions.Descending | UserSortingOptions.Ascending;
  }): Promise<{ results: User[]; totalItems: number }> => {
    let queryParams = `?page=1&pageSize=0`;
    if (!(searchTerm === null || searchTerm === undefined) && searchTerm.trim() !== '') {
      queryParams += `&searchTerm=${searchTerm}`;
    }
    if (orderColumn != null) {
      queryParams += `&sort=${orderColumn}`;
    }
    if (orderDirection != null) {
      queryParams += `&sortDirection=${orderDirection}`;
    }
    return getAuth(`api/users${queryParams}`);
  };

  return {
    loading,
    results,
    fetchUsers,
    fetchAllUsers,
    totalResults
  };
};

export default usePaginatedUsers;
